import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LuxuryBackground, Card, GoldButton, PageHero } from "../components/shared/UIComponents";
import { loanPrograms } from "../data/constants";
import { ChevronDown, ArrowRight, Clock, DollarSign, TrendingUp, Percent } from "lucide-react";
import { usePageNavigate } from "../hooks/usePageNavigate";

const CORE_SLUGS = ["fix_flip", "bridge", "dscr", "cash_out", "construction"];
const ADDITIONAL_SLUGS = ["ground_up", "multifamily", "commercial"];

const BADGES = {
  fix_flip: { text: "MOST POPULAR", color: "bg-teal-100 text-teal-700 border-teal-200" },
  dscr: { text: "GREAT FOR CASHFLOW", color: "bg-teal-100 text-teal-700 border-teal-200" },
};

function ProgramCard({ prog, index }) {
  const { navigateTo } = usePageNavigate();
  const [expanded, setExpanded] = useState(false);
  const Icon = prog.icon;
  const terms = prog.terms || {};
  const badge = BADGES[prog.slug];

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08, duration: 0.5 }}
      data-testid={`program-card-${prog.slug}`}
    >
      <Card>
        <div className="flex items-start gap-4">
          <div className="rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-50 p-4 border border-emerald-200 shrink-0">
            <Icon className="h-7 w-7 text-emerald-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h3 className="text-xl font-bold tracking-tight text-[#0A0A0A]">{prog.title}</h3>
              {badge && (
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${badge.color}`} data-testid={`badge-${prog.slug}`}>
                  {badge.text}
                </span>
              )}
            </div>
            <p className="mt-2 text-sm leading-6 text-[#555]">{prog.description}</p>
          </div>
        </div>

        {/* Key Terms Grid */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {terms.loanRange && (
            <div className="rounded-lg bg-cream border border-cream-warm px-3 py-2">
              <div className="flex items-center gap-1.5 mb-0.5">
                <DollarSign className="h-3 w-3 text-emerald-600" />
                <span className="text-[10px] uppercase tracking-wider text-[#999] font-medium">Loan Range</span>
              </div>
              <div className="text-sm font-semibold text-[#0A0A0A]">{terms.loanRange}</div>
            </div>
          )}
          {terms.term && (
            <div className="rounded-lg bg-cream border border-cream-warm px-3 py-2">
              <div className="flex items-center gap-1.5 mb-0.5">
                <Clock className="h-3 w-3 text-emerald-600" />
                <span className="text-[10px] uppercase tracking-wider text-[#999] font-medium">Term</span>
              </div>
              <div className="text-sm font-semibold text-[#0A0A0A]">{terms.term}</div>
            </div>
          )}
          {(terms.ltv || terms.ltc) && (
            <div className="rounded-lg bg-cream border border-cream-warm px-3 py-2">
              <div className="flex items-center gap-1.5 mb-0.5">
                <Percent className="h-3 w-3 text-emerald-600" />
                <span className="text-[10px] uppercase tracking-wider text-[#999] font-medium">{terms.ltv ? "Max LTV" : "Max LTC"}</span>
              </div>
              <div className="text-sm font-semibold text-[#0A0A0A]">{terms.ltv || terms.ltc}</div>
            </div>
          )}
          {terms.closingSpeed && (
            <div className="rounded-lg bg-cream border border-cream-warm px-3 py-2">
              <div className="flex items-center gap-1.5 mb-0.5">
                <TrendingUp className="h-3 w-3 text-emerald-600" />
                <span className="text-[10px] uppercase tracking-wider text-[#999] font-medium">Closing</span>
              </div>
              <div className="text-sm font-semibold text-[#0A0A0A]">{terms.closingSpeed}</div>
            </div>
          )}
          {terms.dscr && (
            <div className="rounded-lg bg-cream border border-cream-warm px-3 py-2">
              <div className="flex items-center gap-1.5 mb-0.5">
                <TrendingUp className="h-3 w-3 text-emerald-600" />
                <span className="text-[10px] uppercase tracking-wider text-[#999] font-medium">DSCR</span>
              </div>
              <div className="text-sm font-semibold text-[#0A0A0A]">{terms.dscr}</div>
            </div>
          )}
        </div>

        {/* Expandable Details */}
        <button
          onClick={() => setExpanded(!expanded)}
          data-testid={`program-expand-${prog.slug}`}
          className="mt-4 flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-600 transition-colors"
        >
          {expanded ? "Less Details" : "View Details"}
          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
        </button>

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="mt-3 space-y-3 pt-3 border-t border-cream-warm">
                <div className="space-y-2">
                  {prog.bullets.map(b => (
                    <div key={b} className="flex items-start gap-3 text-sm leading-6 text-[#555]">
                      <div className="mt-2 h-1.5 w-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
                {prog.idealFor && (
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-medium mb-1">Ideal For</div>
                    <p className="text-sm text-emerald-800 leading-relaxed">{prog.idealFor}</p>
                  </div>
                )}
                <GoldButton onClick={() => navigateTo("apply")} className="!text-xs !px-5 !py-2" data-testid={`program-apply-${prog.slug}`}>
                  Analyze My Deal <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </GoldButton>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </motion.div>
  );
}

export default function LoanProgramsPage() {
  const { navigateTo } = usePageNavigate();

  const corePrograms = loanPrograms.filter(p => CORE_SLUGS.includes(p.slug));
  const additionalPrograms = loanPrograms.filter(p => ADDITIONAL_SLUGS.includes(p.slug));

  return (
    <div className="relative" data-testid="loan-programs-page">
      <PageHero
        eyebrow="Loan Programs"
        title="Loan Programs Built for Real Estate Investors"
        description="Serving real estate investors across Dallas-Fort Worth, Houston, and Austin. Flexible private capital for your next deal."
      />
      <div className="relative">
        <LuxuryBackground />
        <section className="relative z-10 mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-20 lg:px-8">
          {/* Core Program Grid */}
          <div className="grid gap-6 md:grid-cols-2">
            {corePrograms.map((prog, i) => (
              <ProgramCard key={prog.title} prog={prog} index={i} />
            ))}
          </div>

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-8 text-center"
          >
            <h3 className="text-2xl font-bold text-[#0A0A0A] mb-3">Not Sure Which Program Fits?</h3>
            <p className="text-base text-[#555] max-w-xl mx-auto mb-6">
              Submit your deal details and our marketplace will match you with the optimal financing structure based on your property, timeline, and investment goals.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <GoldButton onClick={() => navigateTo("apply")} data-testid="submit-deal-btn">
                Analyze My Deal <ArrowRight className="h-4 w-4 ml-1" />
              </GoldButton>
              <GoldButton onClick={() => navigateTo("contact")} variant="secondary" data-testid="speak-team-btn">
                Submit Loan Request
              </GoldButton>
            </div>
          </motion.div>

          {/* Additional Programs Section */}
          {additionalPrograms.length > 0 && (
            <div className="mt-20" data-testid="additional-programs-section">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-8">
                <p className="text-xs uppercase tracking-[0.3em] text-emerald-700 font-semibold mb-2">Specialized Lending</p>
                <h2 className="text-2xl font-bold text-[#0A0A0A]">Additional Programs — Available for Qualified Borrowers</h2>
              </motion.div>
              <div className="grid gap-4 sm:grid-cols-3">
                {additionalPrograms.map((prog, i) => {
                  const Icon = prog.icon;
                  return (
                    <motion.div key={prog.slug} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                      data-testid={`additional-program-${prog.slug}`}
                    >
                      <Card className="h-full">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 p-2.5 border border-emerald-200 shrink-0">
                            <Icon className="h-5 w-5 text-emerald-700" />
                          </div>
                          <h3 className="text-base font-bold text-[#0A0A0A]">{prog.title}</h3>
                        </div>
                        <p className="text-sm text-[#555] leading-relaxed mb-4">{prog.description}</p>
                        <button onClick={() => navigateTo("contact")}
                          className="text-sm font-medium text-emerald-700 hover:text-emerald-600 transition-colors flex items-center gap-1.5"
                          data-testid={`additional-contact-${prog.slug}`}>
                          Contact Us to Learn More <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
